import React, { useState, useEffect, useCallback, useRef } from "react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link } from 'react-router-dom';
import Flag from 'react-country-flag';
import "./OneStepMedi.css";

const API_URL = "https://api.securxperts.com:8000";
const ENDPOINT = "/forms/onestepmedi";

const ContactForm = () => {

  const [form, setForm] = useState({
    name: "",
    email: "",
    countrycode: "",
    phone: "",
    location: "",
    promotionType: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Country dropdown states
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const countries = [
    { code: '+852', iso: 'HK', name: 'Hong Kong' },
    { code: '+36', iso: 'HU', name: 'Hungary' },
    { code: '+354', iso: 'IS', name: 'Iceland' },
    { code: '+91', iso: 'IN', name: 'India' },
    { code: '+1', iso: 'US', name: 'USA' },
    { code: '+44', iso: 'GB', name: 'UK' },
    { code: '+61', iso: 'AU', name: 'Australia' },
    { code: '+971', iso: 'AE', name: 'UAE' },
    { code: '+49', iso: 'DE', name: 'Germany' },
    { code: '+33', iso: 'FR', name: 'France' },
    { code: '+81', iso: 'JP', name: 'Japan' },
    { code: '+86', iso: 'CN', name: 'China' },
    { code: '+7', iso: 'RU', name: 'Russia' },
    { code: '+55', iso: 'BR', name: 'Brazil' },
    { code: '+27', iso: 'ZA', name: 'South Africa' },
  ];

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleCountrySelect = (countryCode) => {
    setForm(prev => ({ ...prev, countrycode: countryCode }));
    setIsCountryOpen(false);
    setCountrySearch('');
  };

  const nameFilter = useCallback((value) => {
    return value.replace(/[^a-zA-Z\s]/g, '');
  }, []);

  const phoneFilter = useCallback((value) => {
    return value.replace(/[^0-9]/g, '').slice(0, 10);
  }, []);

  const locationFilter = useCallback((value) => {
    return value.replace(/[^a-zA-Z\s,]/g, '');
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filteredValue = value;

    switch (name) {
      case 'name':
        filteredValue = nameFilter(value);
        break;
      case 'phone':
        filteredValue = phoneFilter(value);
        break;
      case 'location':
        filteredValue = locationFilter(value);
        break;
      case 'email':
        // Email allows standard characters including @ . etc.
        break;
    }

    setForm((prev) => ({ ...prev, [name]: filteredValue }));
  };

  const validateForm = () => {
    if (!form.name?.trim()) return "Name is required";
    if (!form.email?.trim()) return "Email is required";
    if (!form.countrycode?.trim()) return "Country code is required";
    if (!form.phone?.trim()) return "Phone number is required";
    if (!form.location?.trim()) return "Location is required";
    if (!form.promotionType) return "Please select a promotion type";

    // Email format validation
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email)) return "Enter a valid email";

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validateForm();
    if (err) {
      alert(err);
      return;
    }

    setSubmitting(true);

    try {
      const url = `${API_URL}${ENDPOINT}`;
      const payload = {
        name: form.name,
        email: form.email,
        country_code: form.countrycode,
        phoneno: form.phone,
        location: form.location,
        promotion_type: form.promotionType,
      };

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        setShowSuccessPopup(true);
        setForm({
          name: "",
          email: "",
          countrycode: "",
          phone: "",
          location: "",
          promotionType: "",
        });
      } else {
        let errText = `Server responded with ${resp.status}`;
        try {
          const json = await resp.json();
          if (json?.error) errText = json.error;
          else if (json?.message) errText = json.message;
        } catch { }
        alert(errText);
      }
    } catch (e) {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const closePopup = () => {
    setShowSuccessPopup(false);
  };

  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        setShowSuccessPopup(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup]);

  // Success popup
  if (showSuccessPopup) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-xs w-full shadow-2xl relative p-4 animate-in fade-in zoom-in duration-200">
          <button
            onClick={closePopup}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl font-bold p-1 hover:bg-gray-100 rounded-full transition-all w-8 h-8 flex items-center justify-center"
          >
            ×
          </button>

          <div className="text-center pt-2">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-[#063852] mb-1">Success</h3>
            <p className="text-gray-600 text-sm">Form Submitted Successfully.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto bg-white shadow-xl rounded-2xl p-8 border border-gray-200"
    >
      <h3 className="text-3xl font-bold text-center mb-6 text-[#063852]">
        OneStep Medi Promotion Form
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Full Name*"
          maxLength={50}
          className="input"
        />

        <input
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder="Email Address*"
          className="input"
        />

        {/* Custom Country Dropdown - UPDATED TO SHOW FLAG + NAME IN SELECTOR */}
        <div className="relative md:col-span-1">
          <button
            type="button"
            onClick={() => setIsCountryOpen(!isCountryOpen)}
            className="input bg-white flex items-center justify-between w-full pr-8"
          >
            <span className="flex items-center gap-3 truncate flex-1">
              {form.countrycode ? (
                <>
                  <Flag
                    countryCode={countries.find(c => c.code === form.countrycode)?.iso}
                    svg
                    className="w-5 h-3.5 flex-shrink-0"
                  />
                  <span className="truncate">
                    {countries.find(c => c.code === form.countrycode)?.name}
                  </span>
                  <span className="text-sm ml-1">
                    {form.countrycode}
                  </span>
                </>
              ) : (
                <span>Select Country Code</span>
              )}
            </span>
            <svg
              className={`w-5 h-5 transition-transform ${isCountryOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isCountryOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-72 overflow-hidden">
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder="Search country or code..."
                className="w-full p-3 border-b border-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 rounded-t-xl"
                autoFocus
              />
              <div className="max-h-60 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country.code)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                    >
                      <Flag
                        countryCode={country.iso}
                        svg
                        className="w-5 h-3.5 flex-shrink-0"
                      />
                      <span className="flex-1 truncate">{country.name}</span>
                      <span className="text-sm">
                        {country.code}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center text-sm">
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>


        <input
          name="phone"
          value={form.phone}
          onChange={handleChange}
          placeholder="Phone Number*"
          className="input"
        />

        <input
          name="location"
          value={form.location}
          onChange={handleChange}
          placeholder="City, Country*"
          className="input md:col-span-2"
          maxLength={100}
        />

        <select
          name="promotionType"
          value={form.promotionType}
          onChange={handleChange}
          className="input bg-white md:col-span-2"
        >
          <option value="">Select Promotion Type*</option>
          <option value="Clinic">Clinic</option>
          <option value="Hospital">Hospital</option>
          <option value="Doctor">Doctor</option>
          <option value="Clinic + Doctor">Clinic + Doctor</option>
          <option value="Hospital + Doctor">Hospital + Doctor</option>
        </select>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full mt-6 bg-[#063852] hover:bg-[#052c3f] text-white font-semibold py-3 rounded-xl transition-all duration-300 shadow-md disabled:opacity-60"
      >
        {submitting ? "Submitting..." : "Submit Promotion Request"}
      </button>

      <p className="text-xs mt-3 text-gray-500 text-center">
        Your details will remain confidential and used only for enquiry purposes.
      </p>
    </form>
  );
};

// ────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────
export default function OneStepMedi() {
  const SCROLL_SHOW_THRESHOLD = 300;

  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const scrollTimeoutRef = useRef(null);

  const RADIUS = 49;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset =
    CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;

  const onScroll = useCallback(() => {
    const sc = window.scrollY || window.pageYOffset;
    setShowScroll(sc > SCROLL_SHOW_THRESHOLD);

    const doc = document.documentElement;
    const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
    const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;

    setScrollProgress(Math.min(100, Math.max(0, progress)));
    setIsScrolling(true);

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollTimeoutRef.current = null;
    }, 250);
  }, []);

  // ────────────────────────────────────────────────
  // FIXED: Reliable scroll restoration prevention + force top
  // ────────────────────────────────────────────────
  useEffect(() => {
    // Prevent browser from restoring previous scroll position (most important)
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const forceScrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };

    forceScrollToTop();

    // Safety timeouts for browsers that restore scroll late
    const t1 = setTimeout(forceScrollToTop, 0);
    const t2 = setTimeout(forceScrollToTop, 40);
    const t3 = setTimeout(forceScrollToTop, 120);

    // Catch very early unwanted scrolls
    const earlyFix = () => {
      if (window.scrollY > 20) {
        forceScrollToTop();
      }
    };
    window.addEventListener('scroll', earlyFix, { once: true, passive: true });

    // Normal scroll progress listener
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener('scroll', earlyFix);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      // Optional: window.history.scrollRestoration = 'auto';
    };
  }, [onScroll]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToTop();
    }
  };

  return (
    <div className="w-full font-sans text-[#063852] bg-white">
      <Navbar />

      <section className="w-full bg-white pt-10 pb-20 px-6 md:px-20 flex flex-col md:flex-row items-center gap-10">
        <div className="md:w-1/2 mb-12">
          <img src="/img/103.png" alt="Logo" className="w-32 mb-6" />

          <h1 className="text-4xl md:text-5xl font-bold leading-snug">
            All-in-One Healthcare Management System
          </h1>

          <p className="mt-4 text-gray-600 max-w-md ">
            Streamline healthcare management book appointments, access medical records and consult certified doctors, all via a single digital platform.
          </p>
        </div>

        <div className="md:w-1/2 mb-[-85px]">
          <img
            src="/img/onestepheroimg.png"
            alt="Hero"
            className="w-full rounded-2xl shadow-lg"
          />
        </div>
      </section>

      <section className="w-full py-20 px-6 md:px-0 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto mb-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#063852]">
              Experience One Step Medi
            </h2>
            <p className="text-[#063852] opacity-80 mt-3">
              See One Step Medi simplifies hospital management through smart automation and centralised control.A secure, AI-powered platform designed for smoother operations and better efficiency.
            </p>
          </div>
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <video
              className="w-full h-auto max-h-[240px] sm:max-h-[360px] md:max-h-none block"
              src="/video/onestepmedi1.mp4"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              controls
            >
              Your browser does not support HTML5 video.
            </video>
          </div>

        </div>
      </section>

      <section className="w-full px-6 md:px-20 py-4 bg-[#f8fbff]">
        <h2 className="text-3xl font-bold text-center mb-10">
          What We Offer
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {[
            { name: "Pediatrics", icon: "/img/pediatrics.png" },
            { name: "Psychology", icon: "/img/psychology.png" },
            { name: "Daycare", icon: "/img/daycare.png" },
            { name: "Therapy", icon: "/img/therapy.png" },
            { name: "Diagnostics", icon: "/img/diagnostics.png" },
            { name: "Appointments", icon: "/img/appointments.png" },
            { name: "Vaccinations", icon: "/img/vaccinations.png" },
            { name: "Telehealth", icon: "/img/telehealth.png" },
            { name: "Rehabilitation", icon: "/img/rehabilitation.png" },
            { name: "Records", icon: "/img/records.png" },
            { name: "Billing", icon: "/img/billing.png" },
            { name: "Lab Tests", icon: "/img/labtests.png" },
          ].map((item, i) => (
            <div
              key={i}
              className="relative p-6 bg-white rounded-xl text-center shadow-md hover:shadow-xl
              transition-all hover:-translate-y-2 hover:bg-[#e9f6ff]"
            >
              <img
                src={item.icon}
                alt={item.name}
                className="w-10 mx-auto mb-3"
              />
              <p className="font-semibold">{item.name}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-6 md:px-20 py-20 bg-[#e9f4ff]">
        <div className="flex flex-col md:flex-row items-center gap-10">
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-4">Why Trust Our One Step Medi</h2>
            <p className="text-gray-600 mb-6 max-w-md">
              At One Step Medi, we provide expert healthcare and child development services through a seamless digital platform. Our system ensures reliable, efficient care while prioritizing patient safety and satisfaction, making us a trusted partner for families and healthcare providers alike
            </p>
          </div>

          <div className="md:w-1/2">
            <img
              src="/img/img1.png"
              alt="Hiring"
              className="w-full"
            />
          </div>
        </div>
      </section>

      <section className="w-full px-6 md:px-20 py-8 bg-[#f0f7ff]">
        <div className="flex flex-col md:flex-row items-start gap-10">
          <div className="md:w-1/2">
            <img
              src="/img/img2.png"
              alt="Value"
              className="w-full pt-1"
            />
          </div>

          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-6">Essential Capabilities of Our Healthcare Management System</h2>

            <ul className="space-y-4 text-gray-700">
              {[
                "Automates clinical, administrative, and financial workflows to save time and reduce errors.",
                "Manages the complete patient journey from registration to follow-ups.",
                "Centralizes appointments, EMR, billing, pharmacy, laboratory, HR, and payroll modules.",
                "Ensures secure storage and protection of sensitive patient data.",
                "Maintains compliance with HIPAA, NABH, and other healthcare regulations.",
                "Supports real-time reporting and analytics for informed decision-making.",
                "Enables telehealth consultations and remote patient monitoring.",
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-blue-600 text-lg">✔</span> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="w-full px-6 md:px-20 py-12 bg-white">
        <h2 className="text-3xl font-bold text-center mb-10"> Features of Our All-in-One Healthcare Platform</h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {[
            {
              title: "Easy Online Appointment Booking",
              icon: "/img/onlineappoint.png",
            },
            {
              title: "Verified Doctors & Clinics",
              icon: "/img/verifiedclinics.png",
            },
            {
              title: "Advanced Digital Marketing for Doctors",
              icon: "/img/digitalmarketing (1).png",
            },
            {
              title: "Secure Access to Medical History & Reports",
              icon: "/img/medhistory.png",
            },
            {
              title: "Personalised Follow-Ups & Reminders",
              icon: "/img/reminders.png",
            },
            {
              title: "All-in-One Platform for Growth",
              icon: "/img/platformforgrowth.png",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-[#f8fbff] rounded-xl shadow-md text-center"
            >
              <img
                src={item.icon}
                alt={item.title}
                className="w-10 mx-auto mb-3"
              />
              <p className="font-semibold">{item.title}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-6 md:px-20 py-12 bg-[#f8fbff]">
        <h2 className="text-3xl font-bold text-center mb-8">Get in Touch</h2>
        <ContactForm />
      </section>

      <Footer />
      {/* Scroll to Top Button */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer
        flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r
        from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>

          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
}